import React, { useState, useEffect, useMemo } from 'react';
import { CheckCircle, Link, Power, Loader, RefreshCw, ExternalLink } from 'lucide-react';
import apiService from '../../services/apiService';
import { useAuth } from '../../hooks/useAuth';
import { PageLoader } from '../../components/PageLoader';
import Modal from '../../components/Modal';
import hubspotLogo from '../../assets/hubspot-icon.png';
import googleAdsLogo from '../../assets/google-ads-icon.webp';
import goodDayLogo from '../../assets/goodday-logo.png';

const INTEGRATION_LIST = [
    { id: 'googleAnalytics', name: 'Google Analytics', category: 'Google', logo: 'https://www.gstatic.com/analytics-suite/header/suite/v2/ic_analytics.svg' },
    { id: 'searchConsole', name: 'Search Console', category: 'Google', logo: 'https://www.gstatic.com/images/branding/product/1x/search_console_64dp.png' },
    { id: 'googleAds', name: 'Google Ads', category: 'Google', logo: googleAdsLogo },
    { id: 'wordpress', name: 'WordPress', category: 'CMS', logo: 'https://s.w.org/style/images/about/WordPress-logotype-wmark.png' },
    { id: 'hubspot', name: 'HubSpot', category: 'CRM', logo: hubspotLogo },
    // GoodDay card added here
    { id: 'goodday', name: 'GoodDay.work', category: 'Project Management', logo: goodDayLogo },
    // Original "Coming Soon" cards preserved
    { id: 'linkedin', name: 'LinkedIn', category: 'Social', logo: 'https://upload.wikimedia.org/wikipedia/commons/c/ca/LinkedIn_logo_initials.png', disabled: true },
    { id: 'facebook', name: 'Facebook', category: 'Social', logo: 'https://upload.wikimedia.org/wikipedia/commons/5/51/Facebook_f_logo_%282019%29.svg', disabled: true },
];

const IntegrationsView = () => {
    const { user, isLoading: isAuthLoading, refetchUser } = useAuth();
    const [isWpModalOpen, setIsWpModalOpen] = useState(false);
    const [isGoodDayModalOpen, setIsGoodDayModalOpen] = useState(false);
    const [error, setError] = useState('');
    const [gaAccounts, setGaAccounts] = useState([]);
    const [isLoadingGaAccounts, setIsLoadingGaAccounts] = useState(false);
    const [scSites, setScSites] = useState([]);
    const [isLoadingScSites, setIsLoadingScSites] = useState(false);
    const [adsAccounts, setAdsAccounts] = useState([]);
    const [isLoadingAdsAccounts, setIsLoadingAdsAccounts] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.has('connected')) {
            refetchUser();
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, [refetchUser]);

    const activeWorkspace = useMemo(() => {
        if (!user || !user.activeWorkspace || !user.account?.workspaces) return null;
        return user.account.workspaces.find(ws => ws._id.toString() === user.activeWorkspace.toString());
    }, [user]);

    const isGaConnected   = !!activeWorkspace?.settings?.googleRefreshToken;
    const isWpConnected   = !!activeWorkspace?.settings?.wpUrl;
    const isScConnected   = !!activeWorkspace?.settings?.searchConsoleRefreshToken;
    const isAdsConnected  = !!activeWorkspace?.settings?.adwordsRefreshToken;
    const isHsConnected   = !!activeWorkspace?.settings?.hubspotRefreshToken;
    const isGoodDayConnected = !!activeWorkspace?.settings?.goodDayApiKey;

    useEffect(() => {
        if (isGaConnected && activeWorkspace) {
            setIsLoadingGaAccounts(true);
            apiService.fetchAnalyticsAccounts()
                .then(setGaAccounts)
                .catch(err => setError(err.message))
                .finally(() => setIsLoadingGaAccounts(false));
        }
        if (isScConnected && activeWorkspace) {
            setIsLoadingScSites(true);
            apiService.fetchSearchConsoleSites()
                .then(setScSites)
                .catch(err => setError(err.message))
                .finally(() => setIsLoadingScSites(false));
        }
        if (isAdsConnected && activeWorkspace) {
            setIsLoadingAdsAccounts(true);
            apiService.fetchAdWordsCustomers()
                .then(setAdsAccounts)
                .catch(err => setError(err.message))
                .finally(() => setIsLoadingAdsAccounts(false));
        }
    }, [isGaConnected, isScConnected, isAdsConnected, activeWorkspace]);

    // NEW: use the smarter backend route selection
    const handleDisconnect = async (service) => {
        if (!window.confirm(`Are you sure you want to disconnect ${service.name}?`)) return;
        try {
            await apiService.disconnectService(service.id);
            await refetchUser();

            // clear any locally cached lists for Google services
            if (service.id === 'googleAnalytics') setGaAccounts([]);
            if (service.id === 'searchConsole')   setScSites([]);
            if (service.id === 'googleAds')       setAdsAccounts([]);

            // if WordPress was disconnected while its modal is open
            if (service.id === 'wordpress') setIsWpModalOpen(false);
            if (service.id === 'goodday')   setIsGoodDayModalOpen(false);
        } catch (err) {
            alert(`Error: ${err.message}`);
        }
    };

    // Save handler for GoodDay settings
    const handleSaveGoodDay = async (settings) => {
        await apiService.saveGoodDaySettings(settings);
        await refetchUser();
    };

    if (isAuthLoading) return <PageLoader text="Loading user data..." />;
    if (!activeWorkspace) return <PageLoader text="Please select a workspace to manage integrations." />;

    return (
        <>
            <main className="p-8">
                <h1 className="text-4xl font-bold text-white mb-2">Integrations</h1>
                <p className="text-slate-300 text-lg mb-8">Connect your marketing tools to this workspace.</p>

                {error && <p className="text-red-400 p-4 bg-red-900/30 rounded-lg mb-6">{error}</p>}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {INTEGRATION_LIST.map(integration => {
                        let isConnected = false;
                        let onConnectClick = () => {};

                        if (integration.id === 'googleAnalytics') isConnected = isGaConnected;
                        else if (integration.id === 'searchConsole') isConnected = isScConnected;
                        else if (integration.id === 'googleAds') isConnected = isAdsConnected;
                        else if (integration.id === 'wordpress') isConnected = isWpConnected;
                        else if (integration.id === 'hubspot') isConnected = isHsConnected;
                        else if (integration.id === 'goodday') isConnected = isGoodDayConnected;

                        if (integration.category === 'Google') {
                            onConnectClick = () => window.location.href = apiService.getGoogleConnectUrl(integration.id);
                        } else if (integration.id === 'wordpress') {
                            onConnectClick = () => setIsWpModalOpen(true);
                        } else if (integration.id === 'hubspot') {
                            onConnectClick = () => window.location.href = apiService.getHubspotConnectUrl();
                        } else if (integration.id === 'goodday') {
                            onConnectClick = () => setIsGoodDayModalOpen(true);
                        }

                        return (
                            <IntegrationCard
                                key={integration.id}
                                integration={integration}
                                isConnected={isConnected}
                                onConnectClick={onConnectClick}
                                onDisconnect={() => handleDisconnect(integration)}
                                gaAccounts={gaAccounts} isLoadingGaAccounts={isLoadingGaAccounts}
                                scSites={scSites} isLoadingScSites={isLoadingScSites}
                                adsAccounts={adsAccounts} isLoadingAdsAccounts={isLoadingAdsAccounts}
                                activeWorkspace={activeWorkspace}
                                refetchUser={refetchUser}
                                onConfigureGoodDay={() => setIsGoodDayModalOpen(true)}
                            />
                        );
                    })}
                </div>
            </main>

            <WordPressConnectModal
                isOpen={isWpModalOpen}
                onClose={() => setIsWpModalOpen(false)}
                onSuccess={() => { setIsWpModalOpen(false); refetchUser(); }}
            />

            <GoodDayModal
                isOpen={isGoodDayModalOpen}
                onClose={() => setIsGoodDayModalOpen(false)}
                onSave={handleSaveGoodDay}
                initialApiKey={isGoodDayConnected ? '********' : ''}
                initialParentId={activeWorkspace?.settings?.goodDayParentTaskId || ''}
            />
        </>
    );
};

const IntegrationCard = (props) => {
    const { 
        integration, isConnected, onConnectClick, onDisconnect,
        gaAccounts, isLoadingGaAccounts, 
        scSites, isLoadingScSites,
        adsAccounts, isLoadingAdsAccounts,
        activeWorkspace, refetchUser,
        onConfigureGoodDay // Receive the configure handler
    } = props;

    const [selectedGaProperty, setSelectedGaProperty] = useState(activeWorkspace?.settings?.gaPropertyId || '');
    const [selectedScSite, setSelectedScSite] = useState(activeWorkspace?.settings?.searchConsoleSiteUrl || '');
    const [selectedAdsAccount, setSelectedAdsAccount] = useState(activeWorkspace?.settings?.adwordsCustomerId || '');
    const [isSyncing, setIsSyncing] = useState(false);

    useEffect(() => {
        setSelectedGaProperty(activeWorkspace?.settings?.gaPropertyId || '');
        setSelectedScSite(activeWorkspace?.settings?.searchConsoleSiteUrl || '');
        setSelectedAdsAccount(activeWorkspace?.settings?.adwordsCustomerId || '');
    }, [activeWorkspace]);

    const handleGaPropertyChange = async (e) => {
        const newPropertyId = e.target.value;
        setSelectedGaProperty(newPropertyId);
        try {
            await apiService.saveGaProperty(newPropertyId);
            await refetchUser();
        } catch (error) {
            alert(`Error saving property: ${error.message}`);
        }
    };
    
    const handleScSiteChange = async (e) => {
        const newSiteUrl = e.target.value;
        setSelectedScSite(newSiteUrl);
        try {
            await apiService.saveSearchConsoleSite(newSiteUrl);
            await refetchUser();
        } catch (error) {
            alert(`Error saving site: ${error.message}`);
        }
    };

    const handleAdsAccountChange = async (e) => {
        const newCustomerId = e.target.value;
        setSelectedAdsAccount(newCustomerId);
        try {
            await apiService.saveAdWordsCustomer(newCustomerId);
            await refetchUser();
        } catch (error) {
            alert(`Error saving Google Ads account: ${error.message}`);
        }
    };

    const handleWpSync = async () => {
        setIsSyncing(true);
        try {
            const result = await apiService.syncWordPressContent();
            alert(result.message);
        } catch (err) {
            alert(`Sync failed: ${err.message}`);
        } finally {
            setIsSyncing(false);
        }
    };
    
    const handleTestConnection = async (serviceId) => {
        try {
            let result;
            let alertMessage = 'Test successful!';
            switch (serviceId) {
                case 'googleAnalytics':
                    result = await apiService.testGaConnection();
                    alertMessage += ` ${result.message}`;
                    break;
                case 'searchConsole':
                    result = await apiService.testScConnection();
                    alertMessage += ` ${result.message}`;
                    break;
                case 'googleAds':
                    result = await apiService.testAdsConnection();
                    alertMessage += ` ${result.message}`;
                    break;
                case 'hubspot':
                    result = await apiService.testHsConnection();
                    const detailsString = Object.values(result.details || {}).join('\n');
                    alertMessage = `${result.message}\n\n${detailsString}`;
                    break;
                default:
                    alert('No test available for this service.');
                    return;
            }
            alert(alertMessage);
        } catch (err) {
            alert(`Test failed: ${err.message}`);
        }
    };

    const renderConnectionDetails = () => {
        if (!isConnected) return <p className="text-sm text-slate-400 h-full flex items-center">{integration.disabled ? "Coming soon." : `Connect to sync content.`}</p>;

        switch (integration.id) {
            case 'googleAnalytics':
                return (
                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">Analytics Property</label>
                        {isLoadingGaAccounts ? (
                            <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader size={16} className="animate-spin" /> Loading properties...</div>
                        ) : (
                            <select value={selectedGaProperty} onChange={handleGaPropertyChange} className="bg-slate-800 border border-slate-600 text-white text-sm rounded-lg block w-full p-2.5">
                                <option value="">-- Select a Property --</option>
                                {gaAccounts.map(account => (
                                    <optgroup label={account.name} key={account.id}>
                                        {account.properties.map(prop => <option key={prop.id} value={prop.id}>{prop.name}</option>)}
                                    </optgroup>
                                ))}
                            </select>
                        )}
                    </div>
                );
            case 'searchConsole':
                return (
                     <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">Website Property</label>
                        {isLoadingScSites ? (
                            <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader size={16} className="animate-spin" /> Loading sites...</div>
                        ) : (
                            <select value={selectedScSite} onChange={handleScSiteChange} className="bg-slate-800 border border-slate-600 text-white text-sm rounded-lg block w-full p-2.5">
                                <option value="">-- Select a Site --</option>
                                {scSites.map(site => <option key={site.siteUrl} value={site.siteUrl}>{site.siteUrl}</option>)}
                            </select>
                        )}
                    </div>
                );
            case 'googleAds':
                return (
                     <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">Ads Customer Account</label>
                        {isLoadingAdsAccounts ? (
                            <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader size={16} className="animate-spin" /> Loading accounts...</div>
                        ) : (
                            <select value={selectedAdsAccount} onChange={handleAdsAccountChange} className="bg-slate-800 border border-slate-600 text-white text-sm rounded-lg block w-full p-2.5">
                                <option value="">-- Select an Account --</option>
                                {adsAccounts.map(account => <option key={account.id} value={account.id}>{account.descriptiveName} ({account.id})</option>)}
                            </select>
                        )}
                    </div>
                );
            case 'wordpress':
                return (
                    <button onClick={handleWpSync} disabled={isSyncing} className="w-full mt-2 bg-cyan-600/20 text-cyan-400 font-bold py-2 px-4 rounded-md hover:bg-cyan-600/40 flex items-center justify-center gap-2">
                        <RefreshCw size={16} className={isSyncing ? 'animate-spin' : ''} />
                        {isSyncing ? 'Syncing...' : 'Sync Content'}
                    </button>
                );
            case 'hubspot':
                 return (
                    <div className="text-slate-300 text-sm">
                        <p>Your HubSpot account is connected.</p>
                        <p className="text-xs text-slate-500 mt-1">Ready to use in Campaigns and Data Hub.</p>
                    </div>
                );
            // Add case for GoodDay to show a Configure button when connected
            case 'goodday':
                return (
                    <div className="space-y-2">
                    <button
                        onClick={onConfigureGoodDay}
                        className="w-full mt-2 bg-cyan-600/20 text-cyan-400 font-bold py-2 px-4 rounded-md hover:bg-cyan-600/40"
                    >
                        Configure Settings
                    </button>
                    {activeWorkspace?.settings?.goodDayParentTaskId && (
                        <p className="text-xs text-slate-400">
                        Parent Task:&nbsp;
                        <span className="text-slate-300">
                            {activeWorkspace.settings.goodDayParentTaskId}
                        </span>
                        </p>
                    )}
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className={`bg-[#0A142C] rounded-lg border ${isConnected ? 'border-teal-500' : 'border-slate-700'} p-6 flex flex-col`}>
            <div className="flex items-center gap-4">
                <img src={integration.logo} alt={`${integration.name} logo`} className="w-12 h-12 bg-white p-1 rounded-md object-contain" />
                <div>
                    <h3 className="text-xl font-bold text-white">{integration.name}</h3>
                    <p className="text-sm text-slate-400">{integration.category}</p>
                </div>
            </div>
            <div className="flex-grow mt-4">
                 {isConnected && (
                    <div className="flex items-center gap-2 text-teal-400 text-sm font-semibold mb-3">
                        <CheckCircle size={16} /> Connected
                    </div>
                 )}
                 {renderConnectionDetails()}
            </div>
            <div className="mt-4 pt-4 border-t border-slate-700 flex justify-end items-center gap-4">
                {isConnected && (integration.category === 'Google' || integration.id === 'hubspot') && (
                    <button onClick={() => handleTestConnection(integration.id)} className="text-sm font-medium text-yellow-400 hover:text-yellow-300">
                        Test Connection
                    </button>
                )}
                {isConnected ? (
                    <button onClick={onDisconnect} className="text-sm font-medium text-red-500 hover:text-red-400 flex items-center gap-2"><Power size={16} /> Disconnect</button>
                ) : (
                    <button onClick={onConnectClick} disabled={integration.disabled} className="text-sm font-medium text-teal-400 hover:text-teal-300 disabled:text-slate-500 disabled:cursor-not-allowed flex items-center gap-2"><Link size={16} /> Connect</button>
                )}
            </div>
        </div>
    );
};

// GoodDay Modal Component from the new file
const GoodDayModal = ({ isOpen, onClose, onSave, initialApiKey = '', initialParentId = '' }) => {
  const [apiKey, setApiKey] = useState(initialApiKey);
  const [parentTaskId, setParentTaskId] = useState(initialParentId);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (isOpen) {
      // reset values each time the modal opens
      setApiKey(initialApiKey);
      setParentTaskId(initialParentId);
      setError('');
      setSuccess('');
    }
  }, [isOpen, initialApiKey, initialParentId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Build payload carefully: NEVER send the masked placeholder as the apiKey
    const payload = {};
    if (parentTaskId && String(parentTaskId).trim().length > 0) {
      payload.parentTaskId = String(parentTaskId).trim();
    }
    if (apiKey && apiKey !== '********') {
      payload.apiKey = apiKey.trim();
    }

    if (Object.keys(payload).length === 0) {
      setError('Nothing to update. Enter a new API key and/or a Parent Task ID.');
      return;
    }

    try {
      setIsConnecting(true);
      setError('');
      setSuccess('');
      await onSave(payload); // parent calls apiService.saveGoodDaySettings and refetches user
      setSuccess('Settings saved successfully!');
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to save settings.');
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Configure GoodDay.work Integration">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block mb-2 text-sm font-medium text-slate-300">GoodDay API Key</label>
          <input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="Enter GoodDay API Key"
            className="bg-slate-700 border border-slate-600 text-white text-sm rounded-lg block w-full p-2.5"
          />
        </div>

        <div>
          <label className="block mb-2 text-sm font-medium text-slate-300">Parent Task ID</label>
          <input
            type="text"
            value={parentTaskId}
            onChange={(e) => setParentTaskId(e.target.value)}
            placeholder="e.g., 123456"
            className="bg-slate-700 border border-slate-600 text-white text-sm rounded-lg block w-full p-2.5"
          />
          <a
            href="https://help.goodday.work/en/articles/8398325-how-to-find-a-task-id"
            target="_blank"
            rel="noreferrer"
            className="text-xs text-slate-500 hover:text-teal-400 flex items-center gap-1 mt-1"
          >
            How to find a Task ID <ExternalLink size={12} />
          </a>
        </div>

        {error && (
          <p className="text-red-400 text-sm bg-red-900/30 p-3 rounded-md">{error}</p>
        )}
        {success && (
          <p className="text-green-400 text-sm bg-green-900/30 p-3 rounded-md">{success}</p>
        )}

        <div className="flex justify-end gap-2 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary px-4 py-2 rounded-md"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isConnecting}
            className="btn-primary px-4 py-2 rounded-md flex items-center justify-center gap-2"
          >
            {isConnecting ? <Loader className="animate-spin" size={20} /> : 'Save Settings'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

const WordPressConnectModal = ({ isOpen, onClose, onSuccess }) => {
    const [wpUrl, setWpUrl] = useState('');
    const [wpUsername, setWpUsername] = useState('');
    const [applicationPassword, setApplicationPassword] = useState('');
    const [isConnecting, setIsConnecting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsConnecting(true);
        setError('');
        setSuccess('');
        try {
            const result = await apiService.connectWordPress({ wpUrl, wpUsername, wpApplicationPassword: applicationPassword });
            setSuccess(result.message);
            setTimeout(() => {
                onSuccess();
            }, 1500);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsConnecting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Connect WordPress Site">
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block mb-2 text-sm font-medium text-slate-300">Site URL</label>
                    <input type="url" value={wpUrl} onChange={e => setWpUrl(e.target.value)} required className="bg-slate-700 border border-slate-600 text-white text-sm rounded-lg block w-full p-2.5" placeholder="https://example.com" />
                </div>
                <div>
                    <label className="block mb-2 text-sm font-medium text-slate-300">WordPress Username</label>
                    <input type="text" value={wpUsername} onChange={e => setWpUsername(e.target.value)} required className="bg-slate-700 border border-slate-600 text-white text-sm rounded-lg block w-full p-2.5" />
                </div>
                <div>
                    <label className="block mb-2 text-sm font-medium text-slate-300">Application Password</label>
                    <input type="password" value={applicationPassword} onChange={e => setApplicationPassword(e.target.value)} required className="bg-slate-700 border border-slate-600 text-white text-sm rounded-lg block w-full p-2.5" />
                     <a href="https://wordpress.com/support/security/application-passwords/" target="_blank" rel="noopener noreferrer" className="text-xs text-slate-500 hover:text-teal-400 flex items-center gap-1 mt-1">
                        How to generate an Application Password <ExternalLink size={12} />
                    </a>
                </div>
                {error && <p className="text-red-400 text-sm bg-red-900/30 p-3 rounded-md">{error}</p>}
                {success && <p className="text-green-400 text-sm bg-green-900/30 p-3 rounded-md">{success}</p>}
                <div className="flex justify-end gap-2 pt-4">
                    <button type="button" onClick={onClose} className="btn-secondary px-4 py-2 rounded-md">Cancel</button>
                    <button type="submit" disabled={isConnecting} className="btn-primary px-4 py-2 rounded-md flex items-center justify-center gap-2">
                        {isConnecting ? <Loader className="animate-spin" size={20} /> : 'Connect Site'}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default IntegrationsView;